import { InputNumber } from "antd";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { BILLING_CYCLE } from "api/interfaces/subscription.interface";
import { postAddCloudStorage } from "api/planAPI";
import AccountId from "components/atoms/text/labels/AccountId";
import SmallPopupTemplate from "components/templates/modal/small/SmallPopupTemplate";
import useStatusMutation from "hook/useStatusMutation";
import usePlanStatCard from "hook/usePlanStatCard";
import { PlanModal, UsagePlan } from "pages/subscription/SubscriptionStyled";
import { useState } from "react";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import { useAppSelector } from "redux/hooks";
import {
  convertBytesToTB,
  convertTBtoBytes,
  formatNumber,
} from "utils/functions";
import { useQueryClient } from "react-query";
import { PLAN_TYPE } from "api/interfaces/planInterface.interface";
import { isYearlyBillingCycle } from "utils/priceUtils";

interface Props {
  onModal: () => void;
  account: CloudModalAccount;
  cycle: BILLING_CYCLE;
}

export interface CloudModalAccount {
  name: string;
  accountId: string;
  accountNumber: string;
  usedStorage: number;
  totalStorage: number;
  planCloudStorage: number; // 정수 TB 단위
}

const CONTROL_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "Enter",
  "Escape",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

const allowsTwoDecimalInput = (
  raw: string,
  selectionStart: number,
  selectionEnd: number,
  insert: string
) => {
  const next = raw.slice(0, selectionStart) + insert + raw.slice(selectionEnd);
  return /^\d*(\.\d{0,2})?$/.test(next);
};

export default function AddCloudModal(props: Props) {
  const { accountId }: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const queryClient = useQueryClient();

  const { summary } = usePlanStatCard({
    accountId,
    cycle: props.cycle,
    type: PLAN_TYPE.STORAGE,
  });

  const minStorage = Math.ceil(
    convertBytesToTB(props.account.usedStorage ?? 0, true)
  );
  const [storage, setStorage] = useState<number>(minStorage);

  const mutationAddStorage = useStatusMutation(postAddCloudStorage, {
    onSuccess: () => {
      props.onModal();
      ["postPlanUsageStorage", "getPlanEndUser", "postPlanStatCard"].forEach(
        (key) => queryClient.invalidateQueries(key)
      );
    },
    onError: () => {},
  });

  const onConfirm = () => {
    if (!mutationAddStorage.idle) return;
    mutationAddStorage.mutate({
      accountId,
      payload: { storage, accountId: props.account.accountId },
    });
  };

  return (
    <SmallPopupTemplate
      title=""
      onClick={onConfirm}
      cancel={true}
      confirmLabel="Confirm"
      onModal={props.onModal}
    >
      <PlanModal.Wrapper>
        <PlanModal.Title>
          <AccountId
            accountId={props.account.accountNumber}
            level={LEVEL_TYPE.EU}
          />
          <span className="account-name-label">{props.account.name}</span>
          <h3>Add Cloud Storage</h3>
        </PlanModal.Title>
        <PlanModal.StorageWrapper>
          <PlanModal.StorageUsage>
            <label>Cloud Storage Usage</label>
            <b className="storage-usage">
              <span className="storage-usage-section">
                <span className="storage-usage-subtitle">Current + Add</span>
                <span>
                  {formatNumber(props.account.planCloudStorage)}
                  <span className="storage-usage-add">
                    {" "}
                    {storage >= 0 ? "+" : "-"} {storage}
                  </span>{" "}
                  TB
                </span>
              </span>
              <span className="storage-usage-total">
                <span className="storage-usage-subtitle">Total</span>
                <span>
                  {formatNumber(props.account.planCloudStorage + storage, {
                    fractionDigits: 2,
                  })}{" "}
                  TB
                </span>
              </span>
            </b>
          </PlanModal.StorageUsage>
          <PlanModal.InfoItem>
            <label>Add Cloud Storage</label>
            <InputNumber
              value={storage}
              onChange={(value) => {
                if (isNaN(Number(value))) {
                  return;
                }
                setStorage(
                  typeof value === "number" ? value : Number(value) || 0
                );
              }}
              min={minStorage}
              step={1}
              onKeyDown={(e) => {
                const key = e.key;
                if (CONTROL_KEYS.has(key) || e.ctrlKey || e.metaKey) return;
                if (key.length !== 1) return;
                if (key !== "." && !/^\d$/.test(key)) {
                  e.preventDefault();
                  return;
                }

                const input = e.target as HTMLInputElement;
                const raw = input.value ?? "";
                const selectionStart = input.selectionStart ?? raw.length;
                const selectionEnd = input.selectionEnd ?? raw.length;
                if (
                  !allowsTwoDecimalInput(raw, selectionStart, selectionEnd, key)
                )
                  e.preventDefault();
              }}
              parser={(raw) => {
                const normalized = (raw ?? "").replace(/[^\d.]/g, "");
                const [intPart = "", decPart = ""] = normalized.split(".");
                const trimmedDec = decPart.slice(0, 2);
                const next =
                  trimmedDec.length > 0 ? `${intPart}.${trimmedDec}` : intPart;
                const num = Number(next);
                return Number.isFinite(num) ? num : 0;
              }}
            />
          </PlanModal.InfoItem>
        </PlanModal.StorageWrapper>
        <PlanModal.StorageUpdate>
          {isYearlyBillingCycle(props.cycle) && (
            <UsagePlan.Card className="remaining-card">
              <UsagePlan.CardIcon className="remaining-icon">
                <RiMoneyDollarCircleLine />
              </UsagePlan.CardIcon>
              <UsagePlan.CardContent>
                <div className="title">Credits Remaining</div>
                <div className="value">
                  {summary.leftCredit?.remains ?? 0}{" "}
                  <span className="description">
                    {summary.leftCredit?.utilization ?? 0}% utilized
                  </span>
                </div>
              </UsagePlan.CardContent>
            </UsagePlan.Card>
          )}
          <p>
            You will be billed each period for the additional cloud storage
            added to the organization that exceeds the number of storage credits
            purchased.
          </p>
        </PlanModal.StorageUpdate>
      </PlanModal.Wrapper>
    </SmallPopupTemplate>
  );
}
